import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import {
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import {
  CHANNEL_TYPE_LABELS,
  CHANNEL_TYPE_VALUES,
  type AdminDataProvider,
  type ChannelType,
  type MerchantRegion,
  type StoreChannel,
  type StoreTag,
} from '../dataProvider';

/**
 * Bulk assignment on the Stores list (Phase 8, D-multi-channel-editor) — the
 * FIRST bulk-action pattern in the build (every other Datagrid disables them).
 * Select N stores, then attach a tag or add a channel to all of them in one go.
 *
 * THE STORES LIST ROW IS THE PER-MARKET merchant_regions ROW (its id is the
 * merchant_region id), so bulk operates at the per-MARKET grain on exactly the
 * selected, IN-SCOPE rows (the list is already region-scoped by the switcher).
 *
 * EVERY affected row is audited INDIVIDUALLY: bulk tag-attach creates one
 * merchant_region_tags row per store (N rows -> N audit entries), bulk
 * channel-add creates one store_channels row per store, NOT a silent batch. Both
 * respect the unique constraints: a store that already carries the tag, or
 * already has that channel type, is SKIPPED (never duplicated) and reported.
 *
 * Gated `stores:write`: a viewer (read everywhere) sees no bulk buttons.
 */

const BulkTagDialog = ({
  open,
  onClose,
  selectedIds,
}: {
  open: boolean;
  onClose: () => void;
  selectedIds: Array<number | string>;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('stores');
  const { data: vocabulary = [] } = useGetList<StoreTag>('storeTags', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'label', order: 'ASC' },
  });
  const [tagId, setTagId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (tagId === '') return;
    setBusy(true);
    try {
      // Rows that already carry this tag are skipped (the unique join constraint).
      const existing = await dataProvider.getList('merchantRegionTags', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { tagId },
      });
      const have = new Set(
        existing.data.map((j) => Number(j.merchantRegionId)),
      );
      let created = 0;
      let skipped = 0;
      // Sequential so each create lands its OWN audit entry (N rows -> N entries).
      for (const id of selectedIds) {
        if (have.has(Number(id))) {
          skipped += 1;
          continue;
        }
        await dataProvider.create('merchantRegionTags', {
          data: { merchantRegionId: Number(id), tagId },
        });
        created += 1;
      }
      notify(
        `Tag attached to ${created} store(s)` +
          (skipped > 0 ? `, ${skipped} already had it` : ''),
        { type: 'success' },
      );
      unselectAll();
      refresh();
      onClose();
      setTagId('');
    } catch {
      notify('Could not attach the tag', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Attach a tag to {selectedIds.length} store(s)</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The tag is attached to each selected store in its current market.
          Stores that already carry it are left as they are.
        </DialogContentText>
        <FormControl size="small" fullWidth>
          <InputLabel id="bulk-tag-label">Tag</InputLabel>
          <Select
            labelId="bulk-tag-label"
            label="Tag"
            value={tagId === '' ? '' : String(tagId)}
            inputProps={{ 'data-testid': 'bulk-tag-select' }}
            onChange={(e) => setTagId(Number(e.target.value))}
          >
            {vocabulary.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || tagId === ''}
          onClick={apply}
          data-testid="bulk-tag-apply"
        >
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkChannelDialog = ({
  open,
  onClose,
  selectedIds,
}: {
  open: boolean;
  onClose: () => void;
  selectedIds: Array<number | string>;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('stores');
  const [channelType, setChannelType] = useState<ChannelType | ''>('');
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (channelType === '') return;
    setBusy(true);
    try {
      // The selected market rows (for the market + a default rate), and the rows
      // that already have this channel type (skipped, the unique constraint).
      const ids = selectedIds.map((id) => Number(id));
      const regions = await dataProvider.getMany('merchantRegions', { ids });
      const byId = new Map(
        regions.data.map((r) => [Number(r.id), r as MerchantRegion]),
      );
      const existing = await dataProvider.getList('storeChannels', {
        pagination: { page: 1, perPage: 2000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { channelType },
      });
      const have = new Set(
        (existing.data as StoreChannel[]).map((c) =>
          Number(c.merchantRegionId),
        ),
      );
      let created = 0;
      let skipped = 0;
      for (const id of ids) {
        const region = byId.get(id);
        if (!region || have.has(id)) {
          skipped += 1;
          continue;
        }
        await dataProvider.create('storeChannels', {
          data: {
            merchantRegionId: id,
            market: region.market,
            channelType,
            label: CHANNEL_TYPE_LABELS[channelType],
            baseRate: region.baseRate,
            trackingUrl: null,
            attributionMethodOverride: null,
            isActive: true,
            createdAt: new Date().toISOString(),
          },
        });
        created += 1;
      }
      notify(
        `Channel added to ${created} store(s)` +
          (skipped > 0 ? `, ${skipped} already had it` : ''),
        { type: 'success' },
      );
      unselectAll();
      refresh();
      onClose();
      setChannelType('');
    } catch {
      notify('Could not add the channel', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a channel to {selectedIds.length} store(s)</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          A channel of this type is added to each selected store in its current
          market, at the market rate. Stores that already have it are skipped.
        </DialogContentText>
        <FormControl size="small" fullWidth>
          <InputLabel id="bulk-channel-label">Channel type</InputLabel>
          <Select
            labelId="bulk-channel-label"
            label="Channel type"
            value={channelType}
            inputProps={{ 'data-testid': 'bulk-channel-select' }}
            onChange={(e) => setChannelType(e.target.value as ChannelType)}
          >
            {CHANNEL_TYPE_VALUES.map((t) => (
              <MenuItem key={t} value={t}>
                {CHANNEL_TYPE_LABELS[t]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || channelType === ''}
          onClick={apply}
          data-testid="bulk-channel-apply"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const StoreBulkActions = () => {
  const { selectedIds } = useListContext();
  const { role, accessibleRegions } = useAccessContext();
  const [tagOpen, setTagOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);

  // Gated stores:write — a viewer (read everywhere) gets no bulk write actions.
  if (!hasAccess({ role, accessibleRegions }, { capability: 'stores:write' })) {
    return null;
  }

  const ids = (selectedIds ?? []) as Array<number | string>;

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          onClick={() => setTagOpen(true)}
          data-testid="bulk-tag-open"
        >
          Attach tag
        </Button>
        <Button
          size="small"
          onClick={() => setChannelOpen(true)}
          data-testid="bulk-channel-open"
        >
          Add channel
        </Button>
      </Stack>
      <BulkTagDialog
        open={tagOpen}
        onClose={() => setTagOpen(false)}
        selectedIds={ids}
      />
      <BulkChannelDialog
        open={channelOpen}
        onClose={() => setChannelOpen(false)}
        selectedIds={ids}
      />
    </>
  );
};
